import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import { blogsApi } from '../lib/api';
import { extractErrorMessage } from '../lib/utils';
import './DashboardBlogPost.css';

interface Blog {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  category?: string;
  tags: string[];
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  views: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: number;
    name: string;
    email?: string;
  };
}

const DashboardBlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();

  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [] as string[],
    featuredImage: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  });

  const categories = [
    'Cybersecurity',
    'Threat Intelligence',
    'AI & Machine Learning',
    'Network Security',
    'Data Protection',
    'Compliance',
    'Industry News',
    'Best Practices',
    'Case Studies',
    'Product Updates'
  ];

  useEffect(() => {
    const loadBlog = async () => {
      if (id && id !== 'new') {
        try {
          setLoading(true);
          const response = await blogsApi.getBlog(id);
          
          if (response.success && response.data) {
            setBlog(response.data);
            setFormData({
              title: response.data.title || '',
              excerpt: response.data.excerpt || '',
              content: response.data.content || '',
              category: response.data.category || '',
              tags: response.data.tags || [],
              featuredImage: response.data.featuredImage || '',
              status: response.data.status || 'DRAFT'
            });
          } else {
            showError('Blog post not found');
            navigate('/dashboard/blogs');
          }
        } catch (err: any) {
          showError(extractErrorMessage(err));
          navigate('/dashboard/blogs');
        } finally {
          setLoading(false);
        }
      } else if (id === 'new') {
        setIsEditing(true);
        setLoading(false);
      }
    };

    loadBlog();
  }, [id, showError, navigate]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagsChange = (tagsString: string) => {
    const tags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    handleInputChange('tags', tags);
  };

  const handleSave = async (newStatus?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED') => {
    try {
      setSaving(true);
      
      const blogData = {
        ...formData,
        status: newStatus || formData.status,
        published: newStatus === 'PUBLISHED'
      };

      let response;
      if (id === 'new') {
        response = await blogsApi.createBlog(blogData);
        if (response.success) {
          showSuccess('Blog post created successfully!');
          navigate(`/dashboard/blogs/${response.data.id}`);
        }
      } else {
        response = await blogsApi.updateBlog(id!, blogData);
        if (response.success) {
          showSuccess('Blog post updated successfully!');
          setBlog(response.data);
          setIsEditing(false);
        }
      }

      if (!response.success) {
        showError(response.message || 'Failed to save blog post');
      }
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PUBLISHED': return 'dashboard-blog-post-status-published';
      case 'DRAFT': return 'dashboard-blog-post-status-draft';
      case 'ARCHIVED': return 'dashboard-blog-post-status-archived';
      default: return 'dashboard-blog-post-status-draft';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dashboard-blog-post-loading">
          <div className="dashboard-blog-post-loading-spinner">
            <i className="ri-loader-4-line"></i>
          </div>
          <p className="dashboard-blog-post-loading-text">Loading blog post...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-blog-post">
        {/* Header */}
        <div className="dashboard-blog-post-header">
          <div className="dashboard-blog-post-header-content">
            <div className="dashboard-blog-post-breadcrumb">
              <button 
                onClick={() => navigate('/dashboard/blogs')}
                className="dashboard-blog-post-back-btn"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Blogs
              </button>
            </div>

            {!isEditing ? (
              <div className="dashboard-blog-post-title-section">
                <div className="dashboard-blog-post-meta">
                  <span className={`dashboard-blog-post-status ${getStatusBadgeClass(blog?.status || 'DRAFT')}`}>
                    {blog?.status || 'DRAFT'}
                  </span>
                  {blog?.category && (
                    <span className="dashboard-blog-post-category">{blog.category}</span>
                  )}
                </div>
                <h1 className="dashboard-blog-post-title">{blog?.title || 'New Blog Post'}</h1>
                {blog?.excerpt && (
                  <p className="dashboard-blog-post-excerpt">{blog.excerpt}</p>
                )}
              </div>
            ) : (
              <div className="dashboard-blog-post-form-header">
                <h1 className="dashboard-blog-post-title">
                  {id === 'new' ? 'Create New Blog Post' : 'Edit Blog Post'}
                </h1>
              </div>
            )}
          </div>

          <div className="dashboard-blog-post-actions">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="dashboard-blog-post-btn dashboard-blog-post-btn-secondary"
                >
                  <i className="ri-edit-line"></i>
                  Edit
                </button>
                {blog?.status === 'DRAFT' && (
                  <button
                    onClick={() => handleSave('PUBLISHED')}
                    disabled={saving}
                    className="dashboard-blog-post-btn dashboard-blog-post-btn-primary"
                  >
                    <i className="ri-send-plane-line"></i>
                    Publish
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    if (blog) {
                      setFormData({
                        title: blog.title || '',
                        excerpt: blog.excerpt || '',
                        content: blog.content || '',
                        category: blog.category || '',
                        tags: blog.tags || [],
                        featuredImage: blog.featuredImage || '',
                        status: blog.status || 'DRAFT'
                      });
                    }
                  }}
                  disabled={saving}
                  className="dashboard-blog-post-btn dashboard-blog-post-btn-secondary"
                >
                  <i className="ri-close-line"></i>
                  Cancel
                </button>
                <button
                  onClick={() => handleSave('DRAFT')}
                  disabled={saving}
                  className="dashboard-blog-post-btn dashboard-blog-post-btn-outline"
                >
                  <i className="ri-save-line"></i>
                  Save Draft
                </button>
                <button
                  onClick={() => handleSave('PUBLISHED')}
                  disabled={saving}
                  className="dashboard-blog-post-btn dashboard-blog-post-btn-primary"
                >
                  <i className="ri-send-plane-line"></i>
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="dashboard-blog-post-content">
          {!isEditing ? (
            /* View Mode */
            <div className="dashboard-blog-post-view">
              {blog?.featuredImage && (
                <div className="dashboard-blog-post-featured-image">
                  <img src={blog.featuredImage} alt={blog.title} />
                </div>
              )}

              <div className="dashboard-blog-post-view-content">
                <div 
                  className="dashboard-blog-post-text"
                  dangerouslySetInnerHTML={{ __html: blog?.content || '' }}
                />

                {blog?.tags && blog.tags.length > 0 && (
                  <div className="dashboard-blog-post-tags-section">
                    <h3>Tags</h3>
                    <div className="dashboard-blog-post-tags">
                      {blog.tags.map((tag, index) => (
                        <span key={index} className="dashboard-blog-post-tag">
                          <i className="ri-hashtag"></i>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="dashboard-blog-post-info">
                  <div className="dashboard-blog-post-info-item">
                    <i className="ri-eye-line"></i>
                    <span>{blog?.views || 0} views</span>
                  </div>
                  <div className="dashboard-blog-post-info-item">
                    <i className="ri-calendar-line"></i>
                    <span>Created {blog?.createdAt ? formatDate(blog.createdAt) : 'Unknown'}</span>
                  </div>
                  {blog?.updatedAt && blog.updatedAt !== blog.createdAt && (
                    <div className="dashboard-blog-post-info-item">
                      <i className="ri-refresh-line"></i>
                      <span>Updated {formatDate(blog.updatedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <div className="dashboard-blog-post-form">
              <div className="dashboard-blog-post-form-grid">
                <div className="dashboard-blog-post-form-main">
                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Enter blog post title..."
                      className="dashboard-blog-post-form-input"
                    />
                  </div>

                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Excerpt</label>
                    <textarea
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange('excerpt', e.target.value)}
                      placeholder="Brief description of the blog post..."
                      rows={3}
                      className="dashboard-blog-post-form-textarea"
                    />
                  </div>

                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Content</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      placeholder="Write your blog post content here..."
                      rows={20}
                      className="dashboard-blog-post-form-textarea dashboard-blog-post-form-content"
                    />
                  </div>
                </div>

                <div className="dashboard-blog-post-form-sidebar">
                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="dashboard-blog-post-form-select"
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>

                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="dashboard-blog-post-form-select"
                    >
                      <option value="">Select a category</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Featured Image URL</label>
                    <input
                      type="url"
                      value={formData.featuredImage}
                      onChange={(e) => handleInputChange('featuredImage', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="dashboard-blog-post-form-input"
                    />
                    {formData.featuredImage && (
                      <div className="dashboard-blog-post-form-image-preview">
                        <img src={formData.featuredImage} alt="Preview" />
                      </div>
                    )}
                  </div>

                  <div className="dashboard-blog-post-form-group">
                    <label className="dashboard-blog-post-form-label">Tags</label>
                    <input
                      type="text"
                      value={formData.tags.join(', ')}
                      onChange={(e) => handleTagsChange(e.target.value)}
                      placeholder="security, ai, monitoring (comma separated)"
                      className="dashboard-blog-post-form-input"
                    />
                    <div className="dashboard-blog-post-form-help">
                      Separate tags with commas
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="dashboard-blog-post-form-tags-preview">
                        {formData.tags.map((tag, index) => (
                          <span key={index} className="dashboard-blog-post-form-tag">
                            <i className="ri-hashtag"></i>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardBlogPost;